import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import apiService from '../../services/apiService';
import { Save, Package, Box, Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useForm, useFieldArray, Controller } from 'react-hook-form';

interface Product {
  _id: string;
  name: string;
  price: number;
}

type EquipmentType = 'product' | 'kit';

interface FormData {
  type: EquipmentType;
  name: string;
  description: string;
  price: number;
  quantity?: number;
  category?: string;
  components: { productId: string; quantity: number }[];
}

const AddEquipment: React.FC = () => {
  const navigate = useNavigate();
  const [equipmentType, setEquipmentType] = useState<EquipmentType>('product');
  const [products, setProducts] = useState<Product[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, control, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      type: 'product',
      name: '',
      description: '',
      price: 0,
      quantity: 0,
      category: '',
      components: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'components',
  });

  const watchType = watch('type');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await apiService.products.getAll();
        // Make sure you adjust this based on the actual API response structure
        setProducts(response.items || []);
      } catch (error) {
        console.error("Failed to load products:", error);
        toast.error("Erreur lors du chargement des produits.");
      }
    };

    if (watchType === 'kit') {
      fetchProducts();
    }
  }, [watchType]);

  const onSubmit = async (data: FormData) => {
    setIsSaving(true);
    try {
      if (data.type === 'product') {
        const { name, description, price, quantity, category } = data;
        await apiService.products.create({ name, description, price, quantity, category });
        toast.success('Produit créé avec succès !');
      } else { // type is 'kit'
        const { name, description, price, components } = data;
        if (!components || components.length === 0) {
          toast.error('Un kit doit contenir au moins un produit.');
          setIsSaving(false);
          return;
        }
        await apiService.kits.create({ name, description, price, components });
        toast.success('Kit créé avec succès !');
      }
      navigate('/admin/equipments');
    } catch (error: any) {
      console.error('Failed to create equipment:', error);
      toast.error(`Erreur lors de la création : ${error.message || 'Une erreur est survenue.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Package className="w-8 h-8 mr-3 text-primary-600" />
          Ajouter un Équipement
        </h1>

        <Card className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Type d'équipement</label>
              <div className="mt-2 flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="product"
                    {...register('type')}
                    className="form-radio h-4 w-4 text-primary-600"
                  />
                  <span className="ml-2 text-gray-700">Produit</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="kit"
                    {...register('type')}
                    className="form-radio h-4 w-4 text-primary-600"
                  />
                  <span className="ml-2 text-gray-700">Kit</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nom *</label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { required: 'Le nom est requis' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
              </div>
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700">Prix *</label>
                <input
                  type="number"
                  id="price"
                  step="0.01"
                  {...register('price', { required: 'Le prix est requis', valueAsNumber: true })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                />
                {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>}
              </div>
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                id="description"
                {...register('description')}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
              ></textarea>
            </div>

            {watchType === 'product' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Quantité en stock</label>
                  <input
                    type="number"
                    id="quantity"
                    {...register('quantity', { valueAsNumber: true, min: { value: 0, message: 'La quantité ne peut être négative' } })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                  />
                  {errors.quantity && <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>}
                </div>
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">Catégorie</label>
                  <input
                    type="text"
                    id="category"
                    {...register('category')}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                  />
                </div>
              </div>
            )}

            {watchType === 'kit' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Box className="w-6 h-6 mr-2 text-primary-600" />
                  Composer le Kit
                </h3>
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700">Produit</label>
                      <Controller
                        name={`components.${index}.productId`}
                        control={control}
                        rules={{ required: 'Veuillez sélectionner un produit' }}
                        render={({ field }) => (
                          <select {...field} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3">
                            <option value="">Sélectionner un produit</option>
                            {products.map(p => (
                              <option key={p._id} value={p._id}>{p.name} ({p.price.toFixed(2)}€)</option>
                            ))}
                          </select>
                        )}
                      />
                       {errors.components?.[index]?.productId && <p className="mt-1 text-sm text-red-600">{errors.components[index].productId.message}</p>}
                    </div>
                    <div className="w-32">
                      <label className="text-sm font-medium text-gray-700">Quantité</label>
                      <input
                        type="number"
                        {...register(`components.${index}.quantity`, { required: 'Qté requise', valueAsNumber: true, min: { value: 1, message: 'Min 1' } })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      />
                      {errors.components?.[index]?.quantity && <p className="mt-1 text-sm text-red-600">{errors.components[index].quantity.message}</p>}
                    </div>
                    <Button type="button" variant="destructive" size="sm" onClick={() => remove(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="ghost" onClick={() => append({ productId: '', quantity: 1 })}>
                  <Plus className="w-5 h-5 mr-2" />
                  Ajouter un produit au kit
                </Button>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => navigate('/admin/equipments')}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSaving}>
                <Save className="w-5 h-5 mr-2" />
                {isSaving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default AddEquipment;